import {FormEvent} from 'react';
import {useTodoMutations} from '../../api/todos';
import {useMe} from '../../hooks/useAuth';
import {useFormDraft} from '../../hooks/useFormDraft';
import {toast} from '../../hooks/useToast';
import Button from '../Button';
import ErrorBox, {FieldError, isGeneralError} from '../feedback/ErrorBox';
import Sheet from '../feedback/Sheet';
import WhenChips from './WhenChips';
import {addDays, followUpsFor, inDays} from '../../lib/todos';
import {nowLocal, offsetMinutes, todayLocal} from '../../lib/when';
import {Post} from '../../types';

// "Remind me" on a tell: a to-do that follows it. The kind's sensible next checks are offered as
// one tap ("Check for seedlings · in 10 days"); the words and the date can be changed. The to-do
// keeps the tell's plants and place and points back at it, so it shows the tell's photo.
type Props = {post: Post; onClose: () => void};

export default function RemindSheet({post, onClose}: Props) {
    const me = useMe();
    const today = todayLocal();
    const {create} = useTodoMutations();
    const options = followUpsFor(post.kind, post.detail);
    const form = useFormDraft(`remind:${post.id}`, {text: options[0].text, dueLocal: addDays(today, options[0].days) as string | null, tellKind: options[0].tellKind}, {label: 'A reminder', route: `/tell/${post.id}`, listed: false});
    const v = form.values;

    const submit = (event: FormEvent) => {
        event.preventDefault();
        create.mutate(
            {text: v.text.trim(), whenLocal: nowLocal(), whenOffsetMinutes: offsetMinutes(), dueLocal: v.dueLocal, plants: post.plants.map(p => p.label), placeId: post.place?.id ?? null, followsId: post.id, tellKind: v.tellKind},
            {
                onSuccess: () => {
                    form.clear();
                    onClose();
                    toast.success('On your To do list.');
                }
            }
        );
    };

    return (
        <Sheet open onClose={onClose} title="Remind me" busy={create.isPending}>
            <form onSubmit={submit} className="flex flex-col gap-5">
                <div className="flex flex-wrap gap-2" role="group" aria-label="Suggested">
                    {options.map(o => {
                        const due = addDays(today, o.days);
                        return (
                            <button key={`${o.text}${o.days}`} type="button" className="chip" aria-pressed={v.text === o.text && v.dueLocal === due} onClick={() => form.set({text: o.text, dueLocal: due, tellKind: o.tellKind})}>
                                {o.text} · {inDays(o.days)}
                            </button>
                        );
                    })}
                </div>
                <label className="flex flex-col gap-1">
                    <span className="meta">What needs doing?</span>
                    <input className="field" type="text" maxLength={120} required value={v.text} onChange={e => form.set({text: e.target.value})} />
                    <FieldError error={create.error} field="text" />
                </label>
                <WhenChips value={v.dueLocal} onChange={dueLocal => form.set({dueLocal})} today={today} hemisphere={me.data?.hemisphere} />
                <FieldError error={create.error} field="dueLocal" />
                {isGeneralError(create.error) && <ErrorBox error={create.error} />}
                <Button type="submit" busy={create.isPending} busyLabel="Adding…" disabled={v.text.trim() === ''}>
                    Add to To do
                </Button>
            </form>
        </Sheet>
    );
}
